import { Controller, UseFormReturn } from 'react-hook-form';

import { WarehouseIcon } from 'lucide-react';

import { Badge } from 'components/Badge';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Textarea } from 'components/Textarea';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { LoadDateTimeInput } from 'pages/LoadView/LoadInformation/Components';
import { LoadStopsItemTypes } from 'types/Load';
import { datetimeFieldOptions } from 'utils/formValidators';
import { normalizeTimezone } from 'utils/normalizeTimezone';
import { cn } from 'utils/shadcn';

import {
  TRANSPORT_PRO_UPDATE_APPOINTMENT_STATUS_OPTIONS,
  TransportProAppointmentStatus,
} from '../constants';
import { TransportProFormLoad, TransportProSchedulingStop } from '../types';
import {
  appointmentStatusToStopFields,
  formatStopLocationLabel,
  getStopTypeLabel,
} from '../utils';

type StopSchedulingCardProps = {
  stopIndex: number;
  stop: TransportProSchedulingStop;
  formMethods: UseFormReturn<TransportProFormLoad>;
};

export function StopSchedulingCard({
  stopIndex,
  stop,
  formMethods,
}: StopSchedulingCardProps) {
  const { control, setValue, watch } = formMethods;
  const currentLoad = watch();

  const stopLabel = getStopTypeLabel(stop.stopType);
  const isDelivery =
    stop.stopType === LoadStopsItemTypes.Delivery ||
    stop.stopType.toLowerCase() === 'delivery';
  const facilityName = stop.address?.name || '—';
  const locationLabel = formatStopLocationLabel(stop);
  const stopTimezone = normalizeTimezone(
    stop.timezone || stop.address?.timezone
  );

  const statusFieldName = `stops.${stopIndex}.appointmentStatus` as const;

  return (
    <div className='w-full rounded-lg border border-neutral-200 bg-neutral-50 p-4 shadow-sm dark:border-neutral-700 dark:bg-neutral-800/50'>
      <Flex align='start' gap='sm' className='w-full'>
        <Flex direction='col' align='start' gap='sm' className='w-full'>
          <Flex align='center' justify='between' gap='md' className='w-full'>
            <Typography
              variant='body-sm'
              weight='semibold'
              className='truncate'
              title={locationLabel}
            >
              {locationLabel}
            </Typography>
            <Badge
              variant='default'
              className={cn(
                'text-[10px]',
                isDelivery
                  ? 'bg-success-50 border-success text-success'
                  : 'bg-brand-50 border-brand text-brand'
              )}
            >
              {stopLabel}
            </Badge>
          </Flex>

          <Flex align='center' gap='sm'>
            <WarehouseIcon className='w-4 h-4 shrink-0' />
            <Typography
              variant='body-xs'
              weight='bold'
              className='text-neutral-600 leading-3.5'
            >
              {facilityName}
            </Typography>
          </Flex>
        </Flex>
      </Flex>

      <Flex direction='col' gap='md' className='mt-4'>
        <div className='w-full'>
          <Label name='' showAIHint={false} className='text-neutral-500 mb-1'>
            Appointment Status
          </Label>
          <Controller
            name={statusFieldName}
            control={control}
            rules={{
              required: 'Appointment status is required',
              validate: (value) => {
                const { isDirty } = formMethods.getFieldState(statusFieldName);
                return (
                  !isDirty ||
                  value !== TransportProAppointmentStatus.NotRequired ||
                  'Not Required is not valid for appointment updates.'
                );
              },
            }}
            render={({ field, fieldState }) => (
              <>
                <Select
                  value={field.value || ''}
                  onValueChange={(value) => {
                    field.onChange(value);
                    const { apptRequired, apptType } =
                      appointmentStatusToStopFields(value);
                    setValue(`stops.${stopIndex}.apptRequired`, apptRequired, {
                      shouldDirty: true,
                    });
                    setValue(`stops.${stopIndex}.apptType`, apptType, {
                      shouldDirty: true,
                    });
                  }}
                >
                  <SelectTrigger className='w-full'>
                    <SelectValue placeholder='Select appointment status' />
                  </SelectTrigger>
                  <SelectContent>
                    {field.value ===
                    TransportProAppointmentStatus.NotRequired ? (
                      <SelectItem
                        value={TransportProAppointmentStatus.NotRequired}
                        disabled
                      >
                        Not Required
                      </SelectItem>
                    ) : null}
                    {TRANSPORT_PRO_UPDATE_APPOINTMENT_STATUS_OPTIONS.map(
                      (option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {fieldState.error?.message ? (
                  <Typography variant='body-xs' className='text-error-500 mt-1'>
                    {fieldState.error.message}
                  </Typography>
                ) : null}
              </>
            )}
          />
        </div>

        <LoadDateTimeInput
          name={`stops.${stopIndex}.apptStartTime`}
          label='Appointment Start'
          options={datetimeFieldOptions}
          load={currentLoad}
          forceEditable
          defaultTimezone={stopTimezone}
          timezoneOverride={stopTimezone}
          disablePastDates={false}
        />

        <LoadDateTimeInput
          name={`stops.${stopIndex}.apptEndTime`}
          label='Appointment End'
          options={datetimeFieldOptions}
          load={currentLoad}
          forceEditable
          defaultTimezone={stopTimezone}
          timezoneOverride={stopTimezone}
          disablePastDates={false}
        />

        <div className='w-full'>
          <Label name='' showAIHint={false} className='text-neutral-500 mb-1'>
            Internal Notes
          </Label>
          <Controller
            name={`stops.${stopIndex}.apptNote`}
            control={control}
            render={({ field }) => (
              <Textarea
                {...field}
                value={field.value ?? ''}
                placeholder='Add appointment notes for this stop'
                readOnly
                rows={3}
              />
            )}
          />
        </div>

        {stop.timezone ? (
          <Typography variant='body-xs' className='text-neutral-500'>
            Timezone: {stop.timezone}
          </Typography>
        ) : null}
      </Flex>
    </div>
  );
}
